import { useI18n } from '@/hooks/useI18n';
import { canManageWorkspace } from '@/lib/permissions';
import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCommandPalette } from './useCommandPalette';
import {
  Activity,
  Flame,
  LayoutGrid,
  Play,
  RefreshCw,
  Shield,
  Settings,
  Tags,
  UserCheck,
  Users,
} from 'lucide-react';
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
  CommandShortcut,
} from '@/components/ui/command';
import { useAuth } from '@/contexts/AuthContext';
import { useAccounts } from '@/hooks/queries/useAccounts';
import { useCheckNow } from '@/hooks/mutations/useCheckNow';
import {
  useTriggerCycle,
  useTriggerProfileRefresh,
  useTriggerRollupDaily,
  useTriggerRollupHourly,
  useTriggerViralDetection,
} from '@/hooks/mutations/useAdminTriggers';

export function CommandPalette() {
  const { t } = useI18n();
  const { isOpen, close } = useCommandPalette();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === 'ADMIN';
  const accounts = useAccounts({ size: 50 });

  const check = useCheckNow();
  const cycle = useTriggerCycle();
  const profile = useTriggerProfileRefresh();
  const hourly = useTriggerRollupHourly();
  const daily = useTriggerRollupDaily();
  const viral = useTriggerViralDetection();

  const run = useCallback(
    (fn: () => void) => {
      fn();
      close();
    },
    [close],
  );

  return (
    <CommandDialog open={isOpen} onOpenChange={(o) => (o ? null : close())}>
      <CommandInput
        placeholder={t(
          'Naviguer, chercher un compte, déclencher…',
          'Navigate, find an account, run an action…',
        )}
      />
      <CommandList>
        <CommandEmpty>{t('Aucun résultat.', 'No results.')}</CommandEmpty>

        <CommandGroup heading="Navigation">
          <CommandItem onSelect={() => run(() => navigate('/'))}>
            <LayoutGrid className="text-text-3" />
            {t('Aperçu', 'Overview')}
            <CommandShortcut>G · O</CommandShortcut>
          </CommandItem>
          <CommandItem onSelect={() => run(() => navigate('/accounts'))}>
            <Users className="text-text-3" />
            {t('Comptes', 'Accounts')}
          </CommandItem>
          {canManageWorkspace(user?.role) && (
            <>
              <CommandItem onSelect={() => run(() => navigate('/identities'))}>
                <Tags className="text-text-3" />
                {t('Identités', 'Identities')}
              </CommandItem>
              <CommandItem onSelect={() => run(() => navigate('/settings'))}>
                <Settings className="text-text-3" />
                {t('Paramètres', 'Settings')}
              </CommandItem>
            </>
          )}
          {isAdmin && (
            <CommandItem onSelect={() => run(() => navigate('/ops'))}>
              <Activity className="text-text-3" />
              {t('Exploitation', 'Operations')}
            </CommandItem>
          )}
        </CommandGroup>

        {(accounts.data?.content.length ?? 0) > 0 && (
          <>
            <CommandSeparator />
            <CommandGroup heading={t('Comptes', 'Accounts')}>
              {accounts.data?.content.slice(0, 20).map((a) => (
                <CommandItem
                  key={a.id}
                  value={`open ${a.username}`}
                  onSelect={() => run(() => navigate(`/accounts/${a.id}`))}
                >
                  <Users className="text-text-3" />
                  <span>@{a.username}</span>
                </CommandItem>
              ))}
            </CommandGroup>
            <CommandSeparator />
            <CommandGroup heading={t('Scraper maintenant', 'Scrape now')}>
              {accounts.data?.content.slice(0, 20).map((a) => (
                <CommandItem
                  key={a.id}
                  value={`check ${a.username}`}
                  onSelect={() => run(() => check.mutate(a.id))}
                >
                  <RefreshCw className="text-violet" />
                  <span>@{a.username}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </>
        )}

        {isAdmin && (
          <>
            <CommandSeparator />
            <CommandGroup heading="Admin">
              <CommandItem onSelect={() => run(() => cycle.mutate())}>
                <Play className="text-violet" />
                {t('Cycle principal', 'Main cycle')}
                <Shield className="ml-auto h-2.5 w-2.5 text-text-3" />
              </CommandItem>
              <CommandItem onSelect={() => run(() => profile.mutate())}>
                <UserCheck className="text-violet" />
                {t('Refresh profils', 'Profile refresh')}
              </CommandItem>
              <CommandItem onSelect={() => run(() => hourly.mutate({}))}>
                <RefreshCw className="text-violet" />
                {t('Rollup horaire', 'Hourly rollup')}
              </CommandItem>
              <CommandItem onSelect={() => run(() => daily.mutate({}))}>
                <RefreshCw className="text-violet" />
                {t('Rollup journalier', 'Daily rollup')}
              </CommandItem>
              <CommandItem onSelect={() => run(() => viral.mutate())}>
                <Flame className="text-violet" />
                {t('Détection virale', 'Viral detection')}
              </CommandItem>
            </CommandGroup>
          </>
        )}
      </CommandList>
    </CommandDialog>
  );
}
